import { v4 as uuidV4 } from 'uuid';
import { ColumnMapSelect, TraitValue, ColumnItem } from '../../../../types';
import { EventTrait, GroupedTraits } from '@/models/traits';
import { useAppSelector } from '@/reducers';
import { CONSENT_TRAIT, EVENT, TRAITS } from '../../../../constants';
import { DataSource, DataSourceFileConfig, Header } from '@/models/dataSource';

const canonicalize = (name: string): string => {
  return name.toLowerCase().replace(/ /gi, '').replace(/\./gi, '').replace(/_/gi, '').replace(/-/gi, '');
};

export const useColumnMapInitialValues = ({
  fileHeader,
  columnMapSelect,
  groupedTraits,
  defaultNonMatchingTraitId,
}: {
  fileHeader: string[];
  columnMapSelect: ColumnMapSelect[];
  groupedTraits?: GroupedTraits;
  defaultNonMatchingTraitId?: number | string;
}): ColumnItem[] => {
  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceFileConfig>> | undefined;

  const traits = new Map<string, EventTrait>();

  const addTrait = (trait: EventTrait) => {
    const name = canonicalize(trait.dotName);
    traits.set(name, trait);

    if (trait.dotName.startsWith('address_')) {
      traits.set(name.substring(7), trait);
    }
  };

  if (groupedTraits) {
    groupedTraits.customer.forEach((trait) => addTrait(trait));

    if (dataSource?.configuration?.type === EVENT) {
      groupedTraits.event.forEach((trait) => addTrait(trait));
    }
  }

  const getTraitType = (headers: Header): ColumnMapSelect => {
    if (headers.custom) {
      return columnMapSelect[TRAITS.CUSTOM_TRAIT];
    }

    if (headers.traitId === CONSENT_TRAIT) {
      return columnMapSelect[TRAITS.CONSENT_TRAIT];
    }

    return columnMapSelect[TRAITS.MAP_TO_TRAIT];
  };

  const findTrait = (header: string): TraitValue | undefined => {
    const trait = traits.get(canonicalize(header));
    return trait
      ? {
          id: trait.id,
          displayValue: trait.displayName,
        }
      : undefined;
  };

  if (dataSource && dataSource.configuration && dataSource.configuration.headers) {
    return fileHeader.map((header) => {
      const id = uuidV4();

      if (dataSource && dataSource.configuration && dataSource.configuration.headers) {
        const headers = dataSource.configuration.headers.find((dsHeader) => dsHeader.name === header);

        if (headers) {
          if (headers.skip) {
            return { id, header, trait: columnMapSelect[TRAITS.IGNORE_COLUMN] };
          }

          const traitType: ColumnMapSelect = getTraitType(headers);

          return {
            id,
            header,
            trait: traitType,
            traitValue: {
              id: headers.traitId || '',
              displayValue: headers.displayName || '',
            },
          };
        }
      }
      return { id, header, trait: columnMapSelect[0], traitValue: undefined };
    });
  } else {
    return fileHeader.map((header) => {
      const id = uuidV4();
      const matchingTrait = findTrait(header);
      let trait = columnMapSelect[0];
      if (!matchingTrait && defaultNonMatchingTraitId) {
        trait = columnMapSelect.find((trait) => String(trait.id) === String(defaultNonMatchingTraitId)) || columnMapSelect[0];
      }
      return { id, header, trait, traitValue: matchingTrait };
    });
  }
};
